import React, { useState, forwardRef, useImperativeHandle, useContext } from 'react';
import { Button, Box, useTheme } from '@mui/material';
import { FileDownload, FileUpload, Settings } from '@mui/icons-material';
import ImportExportProvider, { ImportExportContext } from './context';
import ImportExportModal from './components/ImportExportModal';
import { ImportExportMAIQProps } from './types/ComponentTypes';

// Define the ref interface
export interface ImportExportMAIQRef {
  openExport: () => void;
  openImport: () => void;
  closeModal: () => void;
}

// Inner component that has access to context
const ImportExportMAIQInner = forwardRef<ImportExportMAIQRef, ImportExportMAIQProps>(({
  type,
  onSuccess,
  onError,
  onImportComplete,
  onExportComplete,
  showImport = true,
  showExport = true,
  modalTitle,
  triggerButton,
  disabled = false
}, ref) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'export' | 'import'>('export');
  
  // Download modal is now handled at component level

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    openExport: () => {
      setActiveTab('export');
      setModalOpen(true);
    },
    openImport: () => {
      setActiveTab('import');
      setModalOpen(true);
    },
    closeModal: () => {
      setModalOpen(false);
    }
  }));

  const handleOpenModal = () => {
    if (disabled) return;
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSuccess = (result: any) => {
    if (onSuccess) {
      onSuccess(result);
    }
    
    // Call specific callbacks
    if (result.type === 'export' && onExportComplete) {
      onExportComplete(result);
    }
    if (result.type === 'import' && onImportComplete) {
      onImportComplete(result);
    }
  };

  const handleError = (error: any) => {
    if (onError) {
      onError(error);
    }
  };

  // Default trigger button
  const defaultTriggerButton = (
    <Button
      variant="contained"
      startIcon={<Settings />}
      onClick={handleOpenModal}
      disabled={disabled}
      sx={{
        backgroundColor: '#0F2A52',
        color: 'white',
        '&:hover': {
          backgroundColor: '#0A1F3F',
        },
        '&:disabled': {
          backgroundColor: 'rgba(15, 42, 82, 0.3)',
          color: 'rgba(255, 255, 255, 0.5)',
        },
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 1.5,
        boxShadow: '0 2px 8px rgba(15, 40, 76, 0.2)',
      }}
    >
      Import/Export
    </Button>
  );

  return (
    <>
      {triggerButton ? (
        <Box onClick={handleOpenModal} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
          {triggerButton}
        </Box>
      ) : (
        defaultTriggerButton
      )}

      <ImportExportModal
        type={type}
        open={modalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        onError={handleError}
        onImportComplete={onImportComplete}
        onExportComplete={onExportComplete}
        showImport={showImport}
        showExport={showExport}
        modalTitle={modalTitle}
        disabled={disabled}
      />
    </>
  );
});

// Main component with provider wrapper
const ImportExportMAIQ = forwardRef<ImportExportMAIQRef, ImportExportMAIQProps>((props, ref) => {
  return (
    <ImportExportProvider config={props.type}>
      <ImportExportMAIQInner ref={ref} {...props} />
    </ImportExportProvider>
  );
});

// Set display name for debugging
ImportExportMAIQ.displayName = 'ImportExportMAIQ';

export default ImportExportMAIQ;

// Export types and utilities
export * from './types';
export { useImportExport } from './context';
export * from './utils';
